import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, Platform } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { CHAT_DEMO, CHAT_MODE_COLOR, IN_HOUSE_MODEL_NAME } from '@/constants/onboarding';

const STEP_MS = 900;
const HOLD_MS = 2400;

/**
 * Looping mini Chat window: a user bubble, a typing beat, then a reply.
 * Visual counterpart to WorkDemoCard so the two mode cards balance.
 */
export default function ChatDemoCard({ compact = false, demo = CHAT_DEMO }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const [phase, setPhase] = useState(0);
  const userText = demo?.user || CHAT_DEMO.user;
  const replyText = demo?.reply || CHAT_DEMO.reply;
  const fade = useRef(new Animated.Value(0)).current;
  const bounce = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    setPhase(0);
  }, [userText]);

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(bounce, { toValue: 1, duration: 320, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
        Animated.timing(bounce, { toValue: 0, duration: 320, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [bounce]);

  useEffect(() => {
    Animated.timing(fade, {
      toValue: phase === 0 ? 0 : 1,
      duration: phase === 0 ? 0 : 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
    const timer = setTimeout(() => {
      setPhase((prev) => (prev >= 2 ? 0 : prev + 1));
    }, phase === 2 ? HOLD_MS : STEP_MS);
    return () => clearTimeout(timer);
  }, [phase, fade]);

  const frameBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const frameBorder = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
  const chromeBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const userBg = isDark ? `${CHAT_MODE_COLOR}33` : `${CHAT_MODE_COLOR}18`;
  const botBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

  return (
    <View style={[styles.frame, { backgroundColor: frameBg, borderColor: frameBorder, flex: 1 }]}>
      <View style={[styles.chrome, { backgroundColor: chromeBg, borderBottomColor: frameBorder }]}>
        <View style={styles.chromeTitle}>
          <View style={[styles.modelDot, { backgroundColor: CHAT_MODE_COLOR }]} />
          <Text size="xxs" color="tertiary" weight="medium">{IN_HOUSE_MODEL_NAME}</Text>
        </View>
        <View style={[styles.liveBadge, { backgroundColor: `${CHAT_MODE_COLOR}22` }]}>
          <Text size="xxs" weight="bold" style={{ color: CHAT_MODE_COLOR, fontSize: 9, letterSpacing: 0.5 }}>
            INSTANT
          </Text>
        </View>
      </View>

      <View style={[styles.body, compact && styles.bodyCompact]}>
        <View style={[styles.bubble, styles.userBubble, { backgroundColor: userBg }]}>
          <Text size="xxs" style={[styles.bubbleText, { color: colors.text_color_primary }]} numberOfLines={compact ? 2 : 3}>
            {userText}
          </Text>
        </View>

        {phase === 1 && (
          <View style={[styles.bubble, styles.botBubble, { backgroundColor: botBg }]}>
            <View style={styles.typingRow}>
              {[0, 1, 2].map((i) => (
                <Animated.View
                  key={i}
                  style={[styles.typingDot, {
                    backgroundColor: CHAT_MODE_COLOR,
                    opacity: bounce.interpolate({
                      inputRange: [0, 1],
                      outputRange: i === 1 ? [1, 0.35] : [0.35, 1],
                    }),
                  }]}
                />
              ))}
            </View>
          </View>
        )}

        {phase === 2 && (
          <Animated.View style={[styles.bubble, styles.botBubble, { backgroundColor: botBg, opacity: fade }]}>
            <Text size="xxs" color="secondary" style={styles.bubbleText} numberOfLines={compact ? 3 : 4}>
              {replyText}
            </Text>
          </Animated.View>
        )}

        <View style={styles.spacer} />

        <View style={[styles.composer, { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderColor: frameBorder }]}>
          <Text size="xxs" color="tertiary" style={{ flex: 1 }}>Ask anything…</Text>
          <View style={[styles.sendBtn, { backgroundColor: CHAT_MODE_COLOR }]}>
            <Icon name="arrow-up" size={9} color="#fff" solid />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  frame: {
    width: '100%',
    minHeight: 168,
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? { boxShadow: '0 10px 30px rgba(0,0,0,0.10)' } : {
      shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.10, shadowRadius: 18,
    }),
  },
  chrome: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderBottomWidth: 1,
  },
  chromeTitle: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  modelDot: { width: 8, height: 8, borderRadius: 4 },
  liveBadge: {
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
  },
  body: { flex: 1, padding: 12, gap: 8 },
  bodyCompact: { padding: 10, gap: 7 },
  bubble: {
    maxWidth: '92%',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
  },
  userBubble: { alignSelf: 'flex-end', borderBottomRightRadius: 4 },
  botBubble: { alignSelf: 'flex-start', borderBottomLeftRadius: 4 },
  bubbleText: { lineHeight: 15 },
  typingRow: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 2 },
  typingDot: { width: 6, height: 6, borderRadius: 3 },
  spacer: { flex: 1, minHeight: 8 },
  composer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 10,
    paddingRight: 4,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  sendBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
